import { useFocusTrap } from "../hooks/useFocusTrap";
import { useEffect, useMemo, useState } from "react";
import {
  SOURCE_CATALOG,
  type SourceEntry,
  type AdapterHealth,
  type SourceStatus,
  type SourceCategory,
} from "@chonburi/shared";
import {
  CATEGORY_LABEL, STATUS_COLOR, STATUS_LABEL, STATUS_MEANING, adapterNameFor,
} from "../lib/sourceCatalog";
// STATUS_COLOR, CATEGORY_LABEL, STATUS_LABEL, API_PATH_TO_ADAPTER, adapterNameFor
// imported from ../lib/sourceCatalog (pure, unit-tested).

import { API_BASE } from "../lib/apiBase";
import { DialogClose } from "./DialogClose";

interface HealthPayload {
  system: { status: string; healthy: number; degraded: number; down: number; total: number };
  adapters: AdapterHealth[];
}

interface Props { open: boolean; onClose: () => void }

export function SourceCatalog({ open, onClose }: Props) {
  const containerRef = useFocusTrap(open);
  const [filter, setFilter] = useState<"all" | SourceStatus>("all");
  const [health, setHealth] = useState<HealthPayload | null>(null);

  // Fetch detailed adapter health when the modal opens. Refetch every 60 s while open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/health/detailed`);
        if (!res.ok) return;
        const json = (await res.json()) as HealthPayload;
        if (!cancelled) setHealth(json);
      } catch {
        // Silent — health surface is best-effort
      }
    };
    load();
    const id = window.setInterval(load, 60_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [open]);

  const healthByAdapter = useMemo(() => {
    const map = new Map<string, AdapterHealth>();
    for (const a of health?.adapters ?? []) map.set(a.name, a);
    return map;
  }, [health]);

  const grouped = useMemo(() => {
    const list = filter === "all"
      ? SOURCE_CATALOG
      : SOURCE_CATALOG.filter((s) => s.status === filter);
    const map = new Map<SourceCategory, SourceEntry[]>();
    for (const s of list) {
      const arr = map.get(s.category) ?? [];
      arr.push(s);
      map.set(s.category, arr);
    }
    return [...map.entries()];
  }, [filter]);

  const counts = useMemo(() => {
    const c = { live: 0, ready: 0, planned: 0, research: 0, stub: 0 } as Record<SourceStatus, number>;
    for (const s of SOURCE_CATALOG) c[s.status]++;
    return c;
  }, []);

  if (!open) return null;

  const sysStatus = health?.system;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div ref={containerRef} className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Source catalog">
        <header className="modal-head catalog-head">
          <h2>Sources <span className="mono">{SOURCE_CATALOG.length}</span></h2>
          <div className="catalog-health mono" role="status" aria-live="polite" aria-label={sysStatus && sysStatus.total > 0 ? `Runtime health: ${sysStatus.healthy} of ${sysStatus.total} tracked healthy, ${sysStatus.degraded} degraded, ${sysStatus.down} down` : "Runtime health unknown"}>
            <span title="Runtime sample in this Worker isolate; not a whole-system census">Sample</span>
            {sysStatus && sysStatus.total > 0 ? <>
              <span title="Healthy / tracked runtime adapters">{sysStatus.healthy}/{sysStatus.total}</span>
              {sysStatus.degraded > 0 && <span className="data-age--warn">· {sysStatus.degraded} degraded</span>}
              {sysStatus.down > 0 && <span className="data-age--crit">· {sysStatus.down} down</span>}
            </> : <span>unknown</span>}
          </div>
          <DialogClose onClose={onClose} label="Close source catalog" />
          <nav className="modal-filter catalog-filters" aria-label="Filter by integration status">
            {(["all", "live", "ready", "planned", "research", "stub"] as const).map((f) => {
              const count = f === "all" ? SOURCE_CATALOG.length : counts[f];
              const label = f === "all" ? "All" : STATUS_LABEL[f];
              return <button
                key={f}
                onClick={() => setFilter(f)}
                aria-label={`${label} ${count} sources`}
                aria-pressed={filter === f}
                className={filter === f ? "active" : ""}
              >
                <span>{label}</span>{f !== "all" && <span className="mono">{count}</span>}
              </button>;
            })}
          </nav>
        </header>

        <div className="modal-body">
          {grouped.map(([cat, items]) => (
            <section key={cat} className="catalog-section">
              <header className="catalog-section-head">
                <span className="eyebrow mono">{CATEGORY_LABEL[cat]}</span>
                <span className="mono caption">{items.length}</span>
              </header>
              <ul className="catalog-list">
                {items.map((s) => {
                  const adapter = adapterNameFor(s.apiPath);
                  const h = adapter ? healthByAdapter.get(adapter) : null;
                  const isMissingKey = h?.lastErrorMessage?.startsWith("Missing ") ?? false;
                  return (
                    <li key={s.id} className="catalog-row">
                      <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                        <div className="catalog-title">
                          <strong>{s.label}</strong>
<span
                        className="catalog-status mono"
                        style={{ color: STATUS_COLOR[s.status] }}
                        aria-label={`Integration status: ${s.status} — ${STATUS_MEANING[s.status]}`}
                        title={STATUS_MEANING[s.status]}
                      >
                        {STATUS_LABEL[s.status]}
                      </span>
                          {h && h.status !== "healthy" && h.status !== "unknown" && (
                            <span
                              className={`mono caption ${h.status === "down" ? "data-age--crit" : "data-age--warn"}`}
                              title={h.lastErrorMessage ?? `Adapter status: ${h.status}`}
                              style={{ marginLeft: 8 }}
                            >
                              {isMissingKey ? `⚠ KEY MISSING${s.keyEnv ? ` · ${s.keyEnv}` : ""}` : `⚠ ${h.status.toUpperCase()}`}
                            </span>
                          )}
                        </div>
                        <div className="catalog-description">
                          {s.describe}
                        </div>
                        <details className="catalog-details"><summary>{s.vendor} · Details</summary><div className="catalog-meta mono caption">
                          {s.apiPath && <span>api {s.apiPath}</span>}
                          {s.endpoint && !s.apiPath && <span>upstream {s.endpoint.replace(/^https?:\/\//, "").slice(0, 56)}</span>}
                          {s.pollSeconds && <span>poll {s.pollSeconds}s</span>}
                          {s.keyEnv && <span>key {s.keyEnv}</span>}
                          {h && h.consecutiveFailures > 0 && (
                            <span style={{ color: "var(--bad)" }}>
                              {h.consecutiveFailures} consec. {h.consecutiveFailures === 1 ? "failure" : "failures"}
                            </span>
                          )}
                        </div></details>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
